import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} NotifyMeRefs
 * @property {HTMLFormElement} form - The back-in-stock form.
 * @property {HTMLInputElement} variantId - Hidden input with the selected variant id.
 * @property {HTMLButtonElement} submitButton - Submit button (inside the dialog for guests).
 * @property {HTMLElement} result - Live region below the button.
 * @property {HTMLDialogElement} [dialog] - Guests: email dialog.
 * @property {HTMLElement} [dialogError] - Guests: error line inside the dialog.
 */

/**
 * Back-in-stock signup for the selected variant. Shown only while that variant is sold out.
 * Logged-in customers sign up with one click; guests enter their email in a dialog.
 * @extends {Component<NotifyMeRefs>}
 */
class NotifyMe extends Component {
  requiredRefs = ['form', 'variantId', 'submitButton', 'result'];

  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();

    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  /**
   * Shows the button for a sold-out variant, hides it otherwise, and follows the variant id.
   * @param {ProductSelectEvent} event
   */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        const resource = detail?.resource;

        this.hidden = !(resource && resource.available === false);
        if (resource) this.refs.variantId.value = String(resource.id);

        this.refs.form.hidden = false;
        this.refs.result.textContent = '';
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[st-notify-me] Event promise rejected:', error);
      });
  };

  openDialog() {
    const { dialog, dialogError } = this.refs;
    if (!dialog) return;

    if (dialogError) dialogError.textContent = '';
    dialog.showModal();
  }

  closeDialog() {
    this.refs.dialog?.close();
  }

  /**
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    const { form, submitButton, result, dialog, dialogError } = this.refs;
    const data = new FormData(form);
    submitButton.disabled = true;
    result.textContent = '';
    if (dialogError) dialogError.textContent = '';

    let message = '';
    try {
      const response = await fetch('/apps/bevaka', { method: 'POST', body: new URLSearchParams(data) });

      if (response.ok) {
        dialog?.close();
        form.hidden = true;
        result.textContent = (this.dataset.messageSuccess ?? '').replace('%EMAIL%', String(data.get('email')));
        return;
      }
      message = this.#errorMessage(response.status);
    } catch {
      message = this.#errorMessage(0);
    } finally {
      submitButton.disabled = false;
    }

    // Guests see errors in the open dialog; logged-in customers below the button.
    if (dialog?.open && dialogError) dialogError.textContent = message;
    else result.textContent = message;
  }

  /**
   * @param {number} status - HTTP status of the signup response, 0 when the request failed.
   * @returns {string}
   */
  #errorMessage(status) {
    if (status === 400) return this.dataset.messageInvalidEmail ?? '';
    if (status === 429) return this.dataset.messageRateLimited ?? '';
    if (status === 422) return this.dataset.messageNotEligible ?? '';
    return this.dataset.messageError ?? '';
  }
}

if (!customElements.get('st-notify-me')) {
  customElements.define('st-notify-me', NotifyMe);
}
